/**
 * GFG-194 (Send Queue, GFG Admin) — unit U10. The per-row three-dot actions menu. This component
 * NEVER calls the API: it fires `onAction(kind, item)` and the panel that owns the six
 * useSendQueue hooks decides what to do. It composes two independent gates on the four mutating
 * items (Pause/Resume, Send now, Reschedule…, Cancel send):
 *   - PERMISSION: can("send", "CAMPAIGN", "all") — a GFG Standard Admin (scope "group") sees only
 *     the three "View" items. The READ of this screen is never gated on CAMPAIGN, so that admin
 *     keeps the page; only the mutating items disappear.
 *   - ROW STATUS: a "Sending" row is already in flight, so the four mutating items are hidden
 *     regardless of permission -- a Super Admin on a Sending row also sees only the View items.
 * The permission check is read ONCE via usePermissions() and used to decide which MenuItem
 * elements to include in a flat array of Menu's direct children -- never wrapped in a gate
 * component. MUI's MenuList walks its direct children to inject the roving tabIndex that drives
 * arrow-key/Tab navigation; a wrapper component (e.g. PermissionGate) breaks that injection and
 * takes every item inside it out of the keyboard focus cycle.
 */
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { type MouseEvent, type ReactNode, useState } from "react";
import { CAMPAIGN_STATUSES } from "@/constants/campaings";
import { COPY } from "@/constants/sendQueueCampaigns";
import { usePermissions } from "@/hooks/usePermissions";
import type { SendQueueItem } from "@/interfaces/sendQueueCampaigns";

const ITEM_SX = {
  px: 1,
  py: "7px",
  cursor: "pointer",
  borderRadius: "6px",
  fontWeight: 500,
  fontSize: 14,
  lineHeight: "20px",
  color: "var(--color-graphite)",
  "&:hover": { bgcolor: "var(--color-light-dust)" },
} as const;

const CANCEL_SEND_ITEM_SX = {
  ...ITEM_SX,
  color: "var(--color-good-wine)",
} as const;

/**
 * One kind per menu item (8 total). Pause and Resume are distinct kinds -- this component already
 * knows the row's status (it chose the label), so it emits the one matching the current status
 * rather than leaving the caller to re-derive it.
 */
export type SendQueueRowActionKind =
  | "pause"
  | "resume"
  | "sendNow"
  | "reschedule"
  | "viewEmail"
  | "viewCampaign"
  | "viewContacts"
  | "cancelSend";

export interface SendQueueRowMenuProps {
  item: SendQueueItem;
  onAction: (kind: SendQueueRowActionKind, item: SendQueueItem) => void;
}

export function SendQueueRowMenu({ item, onAction }: SendQueueRowMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { can, isLoaded } = usePermissions();
  const isSending = item.status === CAMPAIGN_STATUSES.QUEUE_SENDING;
  const isPaused = item.status === CAMPAIGN_STATUSES.QUEUE_PAUSED;

  /**
   * Pause/Resume, Send now, Reschedule… and Cancel send: hidden outright on a Sending row (the
   * send is already in flight for every user), otherwise gated on the "all"-scope send permission.
   * Decided once, up front -- not per item -- so it can be used to build a flat children array
   * instead of wrapping each MenuItem in a gate component.
   */
  const canShowMutatingItems = !isSending && isLoaded && can("send", "CAMPAIGN", "all");

  function openMenu(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function closeMenu() {
    setAnchorEl(null);
  }

  function handleAction(kind: SendQueueRowActionKind) {
    onAction(kind, item);
    closeMenu();
  }

  function menuItem(
    key: string,
    kind: SendQueueRowActionKind,
    label: string,
    sx: SxProps<Theme> = ITEM_SX,
  ) {
    return (
      <MenuItem key={key} sx={sx} onClick={() => handleAction(kind)}>
        {label}
      </MenuItem>
    );
  }

  const menuItems: ReactNode[] = [];
  if (canShowMutatingItems) {
    menuItems.push(
      menuItem(
        "pause",
        isPaused ? "resume" : "pause",
        isPaused ? COPY.rowMenu.resume : COPY.rowMenu.pause,
      ),
    );
    menuItems.push(menuItem("sendNow", "sendNow", COPY.rowMenu.sendNow));
    menuItems.push(menuItem("reschedule", "reschedule", COPY.rowMenu.reschedule));
  }
  menuItems.push(menuItem("viewEmail", "viewEmail", COPY.rowMenu.viewEmail));
  menuItems.push(menuItem("viewCampaign", "viewCampaign", COPY.rowMenu.viewCampaign));
  menuItems.push(menuItem("viewContacts", "viewContacts", COPY.rowMenu.viewContacts));
  if (canShowMutatingItems) {
    menuItems.push(
      menuItem("cancelSend", "cancelSend", COPY.rowMenu.cancelSend, CANCEL_SEND_ITEM_SX),
    );
  }

  return (
    <>
      <IconButton
        onClick={openMenu}
        aria-label={`Actions for ${item.campaignTitle}`}
        sx={{
          width: 30,
          height: 30,
          padding: 0,
          borderRadius: "8px",
          color: "var(--color-graphite)",
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            minWidth: "184px",
            bgcolor: "white",
            border: "1px solid var(--color-smoke)",
            borderRadius: "8px",
            boxShadow: "0 6px 24px rgba(0,0,0,.14)",
            p: "6px",
          },
        }}
      >
        {menuItems}
      </Menu>
    </>
  );
}

export default SendQueueRowMenu;
